// LogLine represents a single log line
export type LogLine = {
    // timestamp when the log line was added
    timestamp: number

    // level determines the severity
    level: string

    // message is the details of the message
    message: string

    // trace connects log lines with others in the same session or request
    trace: string

    // details are any additional details that might need to be included, like ids or similar
    details: unknown | undefined
}

export type MetricDimensionValue = string | number | boolean

// MetricDimension represents a dimension on a metric that would be used to slice it
export type MetricDimension = {
    // name of the dimension
    name: string

    // value to use for dimension
    value: MetricDimensionValue
}

export type MetricDimensions = {
    [name: string]: MetricDimensionValue
}

// MetricMeasurement represents one measurement added to a metric
export type MetricMeasurement = {
    // name of the metric
    name: string

    // dimensions associated with the value
    dimensions: MetricDimension[]

    // the actual value
    value: number
}

export type LogDetailValue = string | number | boolean | undefined

/**
 * LogDetails contains a flat map of keys to values
 * This is important for logging because we don't want to allow deeply nested objects / really large objects to be logged
 */
export type LogDetails = {
    [name: string]: LogDetailValue | LogDetailValue[]
}

// Backend function that takes a log line and figures out where it goes
export type LogFn = (line: LogLine) => void

// Backend function that takes a metric measurement and figures out where it goes
export type MetricFn = (metric: MetricMeasurement) => void

// SystemName is the default rootTrace
export const SystemName = "system"

// rootTrace is used when we don't have a tracing context
let rootTrace: string = SystemName
export const setRootTrace = (newRootTrace: string) => {
    rootTrace = newRootTrace
}

/**
 * Logger interface
 */
export interface Logger {
    /**
     * Log an informational message
     * @param message the message
     * @param details any details to include with the message
     */
    info(message: string, details?: LogDetails): void

    /**
     * Log a warning message
     * @param message the message
     * @param details any details to include with the message
     */
    warn(message: string, details?: LogDetails): void

    /**
     * Log an error message
     * @param message the message
     * @param details any details to include with the message
     */
    error(message: string, details?: LogDetails): void

    /**
     * Extend the logger with a new trace level
     * @param trace the new trace level
     */
    extend(trace: string): Logger
}

/**
 * createLogger creates a logging interface for a backend and trace level
 * @param log log function
 * @param trace the trace to use with this logger
 * @returns Logger using the log function and the trace
 */
function createLogger(log: LogFn, trace?: string): Logger {
    const createLogFn = (level: string) => (message: string, details?: LogDetails) => {
        log({
            timestamp: Date.now(),
            level,
            message,
            trace: trace || rootTrace,
            details,
        })
    }
    return {
        info: createLogFn("info"),
        warn: createLogFn("warn"),
        error: createLogFn("error"),
        extend: (newTrace: string) => {
            if (trace) {
                newTrace = trace + ":" + newTrace
            }
            return createLogger(log, newTrace)
        },
    }
}

export class Measure {
    constructor(private metricFn: MetricFn) {}

    /**
     * Measure a metric
     * @param name of the metric we're adding a value to
     * @param dimensions associated with the value we're adding
     * @param value of what we've measured
     */
    metric(name: string, dimensions: MetricDimensions, value: number): void {
        const dimensionsArray: MetricDimension[] = []
        for (const k in dimensions) {
            dimensionsArray.push({
                name: k,
                value: dimensions[k],
            })
        }

        this.metricFn({
            name,
            dimensions: dimensionsArray,
            value,
        })
    }

    /**
     * capture a duration
     * @param name metric we're adding a duration to
     * @param dimensions dimensions that we want associated with this duration
     * @returns done function; call this when the duration is done
     */
    duration(name: string, dimensions: MetricDimensions): () => void {
        const start = Date.now()
        return () => {
            const value = Date.now() - start
            this.metric(`duration:${name}`, dimensions, value)
        }
    }

    /**
     * capture an action; we only care about the number of these that happen
     * @param name metric that we're adding an action to
     * @param dimensions dimensions that we want associated with this action
     */
    action(name: string, dimensions: MetricDimensions): void {
        this.metric(`action:${name}`, dimensions, 1)
    }
}

export class TelemetryFrontend {
    public logger: Logger
    public measurer: Measure

    constructor(
        private innerLog?: LogFn,
        private innerMetric?: MetricFn
    ) {
        this.logger = createLogger(this.log)
        this.measurer = new Measure(this.measure)
    }

    log = (l: LogLine) => {
        if (this.innerLog) this.innerLog(l)
        else console.log("@nova/telemetry -- log", l)
    }

    measure = (m: MetricMeasurement) => {
        if (this.innerMetric) this.innerMetric(m)
        else console.log("@nova/telemetry -- metric", m)
    }

    setBackend = (newLog?: LogFn, newMetric?: MetricFn) => {
        this.innerLog = newLog
        this.innerMetric = newMetric
    }
}

const { log, measure, logger, measurer, setBackend } = new TelemetryFrontend()

export { log, measure, logger, measurer, setBackend }
